import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { ExtractRouteParams, useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import {
  Alert,
  Asterisk,
  Breakpoints,
  Button,
  Card,
  Checkbox,
  Colors,
  DateInput,
  fontMedium,
  formatDate,
  Icon,
} from "swing-components";

import { AlertError, ContentSingleColumn, useAuth } from "~components";
import { AUTH_URLS, createTimesheetUrl } from "~pages";
import { translate } from "~utils";

import { PaycodeReasons, TimeCardEditView } from "./components";
import { useTimesheet, useTimesheetSubmission } from "./hooks";
import {
  getWeekRangeFromDateTime,
  type NoMealBreakReason,
  type PayCode,
  type PayCodePayload,
  type TimeCard,
  type TimeCardPayload,
} from "./utils";

/**
 * Page | Timesheet Details
 *
 * Render either the TimecardEditView or the PaycodeEditView ana handles
 * submitting changes to the API.
 */
export function TimesheetDetailsPage() {
  /*** Hooks ***/
  const { weekId, indexId } =
    useParams<ExtractRouteParams<typeof AUTH_URLS.timesheetEntryEdit, string>>();
  const { userInfo } = useAuth();
  const queryClient = useQueryClient();
  const history = useHistory();

  /*** Queries / Mutations ***/
  const {
    data: timesheetEntries,
    error,
    isLoading,
  } = useTimesheet({ weekId, now: DateTime.now(), userState: userInfo.address?.state });
  const {
    mutate: submitTimesheet,
    isPending: isSubmitting,
    error: submitError,
  } = useTimesheetSubmission(weekId);

  /*** Constants ***/
  const timesheetEntry = timesheetEntries?.[indexId ? Number(indexId) : -1];
  const timesheetEntryType = timesheetEntry?.type;

  /*** Render #1 - No Timesheet Entry Found */
  if (isLoading === false && timesheetEntry === undefined) {
    return (
      // TODO: @KoltonG - Validate if the title is correct for this situations
      // TODO: Should this be moved into the view so that we can show a storybook
      <ScorePage title="Timesheet Details" isLoading={isLoading} hasBack>
        <ContentSingleColumn>
          {/* TODO: @KoltonG - Validate content */}
          <Alert color="danger" title="Timesheet Entry Not Found">
            {`The timesheet entry you are looking for does not exist. Please go
            back to the timesheet list and try again.`}
          </Alert>
        </ContentSingleColumn>
      </ScorePage>
    );
  }
  // To satisfy TS as this case will never happen.
  if (timesheetEntry === undefined || timesheetEntryType === undefined) return null;

  /*** Handlers ***/
  function handleSubmit(
    formData:
      | TimeCard
      // TODO: @KoltonG - Clean up this type
      | (Omit<PayCode, "date" | "payCode" | "durationInMinutes"> & {
          date: DateTime;
          payCode: NonNullable<PayCode["payCode"]>;
          durationInMinutes: number;
        }),
    complianceReason?: NoMealBreakReason,
  ) {
    let payload: TimeCardPayload | PayCodePayload = {} as TimeCardPayload | PayCodePayload;

    if (formData.type === "TIMECARD") {
      // Get all the original entries that are synced with API (have an ID)
      // and transform all dates to ISO strings
      // TODO: @KoltonG - This might be too stringent of a check
      let originalEntries = (timesheetEntry as TimeCard).entries
        .filter((entry) => entry.id && entry.type && entry.ts)
        .map((entry) => ({
          ...entry,
          type: entry.type!,
          ts: entry.ts!.toUTC().toISO(),
        }));
      // Get all the form entries and transform all dates to ISO strings
      const formEntries = formData.entries.map((entry) => ({
        ...entry,
        // TODO: @KoltonG - This is a strong assumption
        type: entry.type!,
        ts: entry.ts!.toUTC().toISO({
          // We are removing milliseconds since they cannot be set by the user.
          suppressMilliseconds: true,
        }),
      }));

      // Create a new array to store the merged entries
      const mergedEntries: TimeCardPayload["entries"] = [];

      // For each form entry
      formEntries.forEach((entry) => {
        // If the entry has an ID, this this is an UPDATE action
        if (entry.id) {
          // Remove the entry from the original entries since it's been edited
          originalEntries = originalEntries.filter(
            (originalEntry) => originalEntry.id !== entry.id,
          );
          // Add the form entry to the merged entries
          mergedEntries.push({
            ...entry,
            action: "UPDATE" as const,
          });
        }
        // If the entry does not have an ID, this is a CREATE action
        else {
          // Add the form entry to the merged entries
          mergedEntries.push({
            type: entry.type!,
            ts: entry.ts!,
            action: "CREATE" as const,
          });
        }
      });

      // For each of the remaining original entries
      originalEntries.forEach((entry) => {
        // Since this entry was not in the form, it must be a DELETE action
        mergedEntries.push({
          ...entry,
          action: "DELETE" as const,
        });
      });

      payload = {
        type: "TIMECARD",
        date: formData.date.toISODate(),
        request: {
          ...(timesheetEntry as TimeCard).request,
          startTime: (timesheetEntry as TimeCard).request.startTime.toISO(),
          endTime: (timesheetEntry as TimeCard).request.endTime.toISO(),
        },
        school: (timesheetEntry as TimeCard).school,
        entries: mergedEntries,
        // Include CA meal break compliance reason only if it's a valid API enum value
        ...(complianceReason && {
          noMealBreakReason: complianceReason as "SUB_WORKED_MEAL_BREAK" | "SUB_WAIVED_MEAL_BREAK",
        }),
      };
    } else {
      payload = {
        ...formData,
        payCode:
          formData.payCode === "Sick" && userInfo.address?.state === "NJ"
            ? "NJSIC"
            : formData.payCode,
        date: formData.date.toISODate(),
        action: formData.id ? "UPDATE" : "CREATE",
      };
    }

    submitTimesheet(
      { timesheet: [payload] },
      {
        onSuccess: () => {
          queryClient.removeQueries({ queryKey: ["timesheets", weekId] });
          history.push(createTimesheetUrl(weekId));
        },
        onError: (error) => {
          // TODO: Validate why this could be failing
          console.error("Failed to submit timesheet:", error);
        },
      },
    );
  }

  /*** Render #2 - TimeCard ***/
  if (timesheetEntryType === "TIMECARD") {
    return (
      <TimeCardEditView
        timeCard={timesheetEntry}
        error={!!error || !!submitError}
        isLoading={isLoading || isSubmitting}
        userState={userInfo.address?.state}
        onSubmit={handleSubmit}
      />
    );
  }

  /*** Render #3 - PayCode ***/
  return (
    <PayCodeEditView
      payCode={timesheetEntry}
      error={!!error || !!submitError}
      isLoading={isLoading || isSubmitting}
      onSubmit={handleSubmit}
    />
  );
}

/***** Types *****/
type PayCodeEditViewProps = {
  payCode: PayCode;
  /** @default false */
  error?: boolean;
  isLoading?: boolean;
  onSubmit: (
    formData: Omit<PayCode, "date" | "payCode" | "durationInMinutes"> & {
      date: DateTime;
      payCode: NonNullable<PayCode["payCode"]>;
      durationInMinutes: number;
    },
  ) => void;
};

/**
 * View | PayCodeEdit
 *
 * Renders a form to edit a PAYCODE entry.
 *
 * This is kinda doing creation and editing.
 */
export function PayCodeEditView(props: PayCodeEditViewProps) {
  const { payCode, error = false, isLoading, onSubmit } = props;

  /*** Form State ***/
  const [form, setForm] = useState<
    Omit<PayCode, "durationInMinutes"> & {
      isCertified: boolean;
      durationInMinutes: number;
    }
  >({
    ...payCode,
    durationInMinutes: payCode.durationInMinutes || 0,
    isCertified: false,
  });

  /*** Handlers ***/
  function handleHoursChange(newHours: number) {
    const hasMinutes = form.durationInMinutes % 60 > 0;
    const validHours = Math.max(0, Math.min(hasMinutes ? 23 : 24, Math.floor(newHours)));

    setForm((prev) => {
      const originalMinutes = prev.durationInMinutes % 60;
      const newDurationInMinutes = validHours * 60 + originalMinutes;

      return {
        ...prev,
        durationInMinutes: newDurationInMinutes,
      };
    });
  }

  function handleMinuteChange(newMinutes: number) {
    const validMinutes = Math.max(0, Math.min(59, Math.floor(newMinutes)));

    setForm((prev) => {
      const originalHours = Math.floor(prev.durationInMinutes / 60);
      const newDurationInMinutes = originalHours * 60 + validMinutes;

      return {
        ...prev,
        durationInMinutes: newDurationInMinutes,
      };
    });
  }

  /**
   * Handles form submission by calling the parent handler with form data
   */
  function handleSubmit() {
    const tempForm = { ...form };
    // Remove the isCertified field from the form data
    delete (tempForm as Partial<typeof form>).isCertified;
    onSubmit(
      tempForm as Omit<PayCode, "date" | "payCode" | "durationInMinutes"> & {
        date: DateTime;
        payCode: NonNullable<PayCode["payCode"]>;
        durationInMinutes: number;
      },
    );
  }

  /*** Constants */
  const { weekStart, weekEnd } = getWeekRangeFromDateTime(DateTime.now().toISODate());
  const nextDay = weekEnd.plus({ days: 1 }).toISODate();
  const previousDay = weekStart.minus({ days: 1 }).toISODate();

  const isFormValid =
    !!form.date &&
    !!form.payCode &&
    form.durationInMinutes !== undefined &&
    form.durationInMinutes > 0 &&
    form.isCertified;
  const pageTitle = payCode.date
    ? formatDate({ date: payCode.date, format: "Wed, Jan 1" })
    : "Create Time Entry";

  /*** Render ***/
  return (
    <ScorePage title={pageTitle} isLoading={isLoading} hasBack>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 24,
            padding: 16,
            [Breakpoints.mediumDeviceAndUp]: { padding: 0 },
          }}
        >
          {/* Error Display */}
          {error && (
            <AlertError
              // TODO: This looks odd
              margin="0px"
            />
          )}

          {/* Paycode Reasons */}
          <PaycodeReasons
            reason={form.payCode}
            // Disable changing reason if there is an ID
            isDisabled={!!form.id}
            onReasonChange={(reason) => setForm((prev) => ({ ...prev, payCode: reason }))}
          />

          {/* Date */}
          <Card>
            <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                {translate("LABEL_DATE")}
                <Asterisk />
              </p>
              {/* Can only select within the current interval range */}
              <DateInput
                value={form.date?.toISO()}
                onChange={(newDate) =>
                  setForm((prev) => ({ ...prev, date: DateTime.fromISO(newDate) }))
                }
                disabledDateRanges={[
                  { start: "1900-01-01", end: previousDay },
                  { start: nextDay, end: "2100-12-31" },
                ]}
                // Disabled when the original date is set
                isDisabled={!!payCode.date}
              />
            </div>
          </Card>

          {/* Hours * Minutes */}
          <Card>
            {/* Label */}
            <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="Clock" />
              <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                {translate("TIMECARD_ENTRY_FORM_TIME_LABEL")}
                <Asterisk />
              </p>
            </div>
            {/* Hours */}
            <div
              css={{
                padding: "16px 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <p css={{ margin: 0 }}>Hours</p>
              <input
                type="number"
                value={
                  form.durationInMinutes ? Math.floor(form.durationInMinutes / 60).toString() : "0"
                }
                onChange={(e) => handleHoursChange(parseInt(e.target.value) || 0)}
                min="0"
                // When there is at least 1 minutes, then limit to 23 hours
                max={form.durationInMinutes % 60 > 0 ? "23" : "24"}
                css={{
                  ...fontMedium(),
                  width: 90,
                  textAlign: "center",
                  padding: "8px",
                  border: `1px solid ${Colors.slate300}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  backgroundColor: "transparent",
                }}
              />
            </div>

            {/* Minutes */}
            <div
              css={{
                padding: "16px 0",
                borderTop: `1px solid ${Colors.slate200}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <p css={{ margin: 0 }}>Minutes</p>
              <input
                type="number"
                value={(form.durationInMinutes % 60).toString()}
                onChange={(e) => handleMinuteChange(parseInt(e.target.value) || 0)}
                min="0"
                max="59"
                css={{
                  ...fontMedium(),
                  width: 90,
                  textAlign: "center",
                  padding: "8px",
                  border: `1px solid ${Colors.slate300}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  backgroundColor: "transparent",
                }}
              />
            </div>
          </Card>

          {/* Certification Checkbox */}
          <Card>
            <div css={{ marginLeft: -16, marginRight: -16 }}>
              <Checkbox
                label={translate("TIMECARD_ENTRY_CERTIFICATION_CHECKBOX_LABEL")}
                isChecked={form.isCertified}
                onCheckedChange={(isCertified) => setForm((prev) => ({ ...prev, isCertified }))}
              />
            </div>
          </Card>

          {/* Submit Button */}
          <div
            css={{
              display: "flex",
              justifyContent: "center",
              [Breakpoints.mediumDeviceAndUp]: {
                justifyContent: "flex-end",
              },
            }}
          >
            <Button
              type="primary"
              onClick={handleSubmit}
              isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
              status={!isFormValid ? "disabled" : "enabled"}
            >
              {translate("TIMESHEET_SUBMIT_ENTRY_LABEL")}
            </Button>
          </div>
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}
